import React, { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";

/* ─── Document icon (Figma 138:4155) ─── */
const DocIconSvg = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M4 2C4 1.44772 4.44772 1 5 1H12L16 5V18C16 18.5523 15.5523 19 15 19H5C4.44772 19 4 18.5523 4 18V2Z"
      stroke="#9CA3AF"
      strokeWidth="1.4"
      strokeLinejoin="round"
      fill="none"
    />
    <path d="M12 1V5H16" stroke="#9CA3AF" strokeWidth="1.4" strokeLinejoin="round" fill="none" />
  </svg>
);

/* ─── Enter-key icon (Figma 139:2155) ─── */
const EnterIconSvg = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M11.5 3V8.5H4.5M4.5 8.5L7 6M4.5 8.5L7 11"
      stroke="#5B5853"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* ─── Styled components ─── */

const DividerLine = styled.div`
  width: 100%;
  height: 1px;
  background: rgba(0, 0, 0, 0.08);
`;

const SuggestionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 4px;
  as: 4px;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: #f6f5f4;
  }
`;

const SuggestionText = styled.span`
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #525252;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ViewAllRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 3px;
  border-radius: 4px;
  cursor: pointer;
`;

const ViewAllText = styled.span`
  font-family: "Inter", sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #525252;
  text-decoration: underline;
  text-decoration-style: dotted;
  text-underline-offset: 2px;
`;

const AIHintRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 5px;
  padding: 0 6px;
`;

const HintText = styled.span`
  font-family: "Inter", sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 18px;
  color: #5b5853;
  text-align: center;
  white-space: nowrap;
`;

const EnterBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  background: #f6f5f4;
  border-radius: 4px;
  padding: 4px;
`;

const EnterBadgeText = styled.span`
  font-family: "Inter", sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 18px;
  color: #5b5853;
`;

const NoResultsMessage = styled.div`
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #64748b;
  text-align: center;
  padding: 16px 16px 12px;
`;

const NoResultsFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 5px;
  padding: 0 6px 12px;
`;

/* ─── Helper: extract plain text from HTML title ─── */
function extractPlainTitle(htmlTitle) {
  if (!htmlTitle) return "";
  const span = document.createElement("span");
  span.innerHTML = htmlTitle;
  return (span.textContent || span.innerText || "").trim();
}

/* ─── Main component ───
   Renders a SINGLE unified card (portal) that starts at the input's top edge.
   - Top portion (input height) is a transparent spacer → clicks pass to real input.
   - Bottom portion is the suggestions list → interactive.
   The card carries the Figma shadow and border-radius so it looks like one element. */

export default function SearchSuggestionsDropdown({
  inputRef,
  items,
  onSelect,
  onViewAll,
  isAIEnabled,
  onClose,
}) {
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0, inputHeight: 0 });
  const cardRef = useRef(null);

  const updatePosition = useCallback(() => {
    if (!inputRef?.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    const cs = window.getComputedStyle(inputRef.current);
    const tlr = parseFloat(cs.borderTopLeftRadius) || 10;
    setPos({
      top: rect.top,
      left: rect.left,
      width: rect.width,
      inputHeight: rect.height,
      radius: tlr,
    });
  }, [inputRef]);

  useEffect(() => {
    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [updatePosition]);

  /* Close on click outside */
  useEffect(() => {
    const handleMouseDown = (e) => {
      if (
        cardRef.current &&
        !cardRef.current.contains(e.target) &&
        inputRef?.current &&
        !inputRef.current.contains(e.target)
      ) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [onClose, inputRef]);

  /* Close on Escape */
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const hasNoResults = !items || items.length === 0;
  const suggestions = hasNoResults ? [] : items.slice(0, 3);

  return createPortal(
    <div
      ref={cardRef}
      style={{
        position: "fixed",
        top: pos.top,
        left: pos.left,
        width: pos.width,
        zIndex: 9998,
        borderRadius: `${pos.radius}px`,
        background: "#ffffff",
        boxShadow:
          "0px 0px 2px rgba(0, 0, 0, 0.1), 0px 2px 4px rgba(0, 0, 0, 0.1)",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      {/* Spacer matching the real input — clicks pass through to the real input behind */}
      <div style={{ height: pos.inputHeight, pointerEvents: "none" }} />

      {/* Divider between input area and suggestions (Figma 138:4153) */}
      <DividerLine />

      {/* Suggestions list or no-results message (Figma 138:4152) */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 6,
          padding: "8px 16px 12px",
          pointerEvents: "auto",
        }}
      >
        {hasNoResults ? (
          <>
            <NoResultsMessage>
              {isAIEnabled
                ? "We're sorry. No results were found, Try asking AI"
                : "We're sorry. No results were found."}
            </NoResultsMessage>
            {isAIEnabled && (
              <NoResultsFooter>
                <HintText>Press</HintText>
                <EnterBadge>
                  <EnterBadgeText>Enter</EnterBadgeText>
                  <EnterIconSvg />
                </EnterBadge>
                <HintText>to ask AI</HintText>
              </NoResultsFooter>
            )}
          </>
        ) : (
          <>
            {suggestions.map((item, i) => (
              <SuggestionRow
                key={item.articleId || i}
                onClick={() => onSelect(item, i)}
              >
                <DocIconSvg />
                <SuggestionText>{extractPlainTitle(item.title)}</SuggestionText>
              </SuggestionRow>
            ))}

            {isAIEnabled && (
              <ViewAllRow onClick={onViewAll}>
                <ViewAllText>View all results</ViewAllText>
              </ViewAllRow>
            )}

            {isAIEnabled && (
              <AIHintRow>
                <HintText>Press</HintText>
                <EnterBadge>
                  <EnterBadgeText>Enter</EnterBadgeText>
                  <EnterIconSvg />
                </EnterBadge>
                <HintText>to ask AI</HintText>
              </AIHintRow>
            )}
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
